import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const BrandsSection = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Trusted by Leading Brands',
      subtitle: 'We proudly partner with world-renowned fashion and textile brands'
    },
    tr: {
      title: 'Önde Gelen Markalar Tarafından Tercih Ediliyor',
      subtitle: 'Dünya çapında tanınmış moda ve tekstil markaları ile gurulla çalışıyoruz'
    }
  };

  const text = content[language];

  // Brand logos
  const brands = [
    { name: 'Decathlon', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/inhr8w4t_DECATHLON.png' },
    { name: 'Ellesse', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/42vfu7oc_ellesse.png' },
    { name: 'FC Bayern München', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/ifoy288s_fc%20bayern.png' },
    { name: 'Finisterre', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/as9tbkty_finisterre.png' },
    { name: 'GAP', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/4urvo12v_GAP.png' },
    { name: 'Happy Socks', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/wxo5paje_happy%20socks.png' },
    { name: 'Hummel', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qs02t54n_hummel.png' },
    { name: 'IKEA', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/98j0qlg4_IKEA.png' },
    { name: 'KAFT', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/1o99uci2_KAFT.png' },
    { name: 'Lee Cooper', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/0kysdttr_Lee%20Cooper.png' },
    { name: 'MAAS', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/04ly8ppg_maas.png' },
    { name: 'Marks & Spencer', logo: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/2znei215_Marks%20Spencer.png' },
    { name: 'Mini Rodini', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/iml9xg4g_minirodini.png' },
    { name: 'Toad&Co', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/pb7zuqv8_toad%20co.png' },
    { name: 'Tommy Hilfiger', logo: 'https://customer-assets.emergentagent.com/job_etiteks-textile/artifacts/iixd3evq_tommy.png' }
  ];

  // Logo component for reuse
  const LogoItem = ({ brand, isMobile = false }) => (
    <div className={`flex-shrink-0 ${isMobile ? 'mx-4' : 'mx-0'}`}>
      <img 
        src={brand.logo} 
        alt={brand.name}
        className={`object-contain ${isMobile ? 'h-6 w-20' : 'w-full h-16 md:h-20'}`}
        style={{ 
          filter: 'grayscale(100%) opacity(0.7)'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.filter = 'grayscale(0%) opacity(1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.filter = 'grayscale(100%) opacity(0.7)';
          }
        }}
      />
    </div>
  );

  return (
    <section id="brands" className="py-12 lg:py-24 bg-white">
      {/* Mobile & Tablet: Light background with marquee */}
      <div className="lg:hidden bg-gray-100 py-4 overflow-hidden">
        <div className="text-center mb-3 px-4">
          <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">
            {text.title}
          </h2>
        </div>
        
        {/* Infinite scrolling marquee */}
        <div className="relative overflow-hidden">
          <div className="flex brands-marquee whitespace-nowrap items-center">
            {/* First set of logos */}
            {brands.map((brand, index) => (
              <LogoItem key={`first-${index}`} brand={brand} isMobile={true} />
            ))}
            {/* Duplicate set for seamless loop */}
            {brands.map((brand, index) => (
              <LogoItem key={`second-${index}`} brand={brand} isMobile={true} />
            ))}
          </div>
        </div>
      </div>

      {/* Desktop: Original grid layout */}
      <div className="hidden lg:block max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {text.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {text.subtitle}
          </p>
        </div>

        {/* Brands Grid - Desktop only */}
        <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-8">
          {brands.map((brand, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-xl p-4 lg:p-6 flex items-center justify-center hover:bg-gray-50 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 cursor-pointer border border-gray-100 shadow-sm"
            >
              <img 
                src={brand.logo} 
                alt={brand.name}
                className="w-full h-16 lg:h-20 object-contain transition-all duration-500 group-hover:scale-110"
                style={{ 
                  filter: 'grayscale(100%) opacity(0.7)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'grayscale(0%) opacity(1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'grayscale(100%) opacity(0.7)';
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandsSection;
